import type { HTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef, useRef } from 'react';
import styled, { css } from 'styled-components';
import type { PositionWithOffsetValue } from '@elfsight/configurator-contracts';
import { useDelayedUnmount, useWindowKeydown } from '@elfsight/lib-utils';
import useMergedRef from '@react-hook/merged-ref';
import { Portal } from '../portal';
import { zIndexLayer } from '../zIndexLayer';
import { positionMixin } from './mixins';
import { Shadow } from './shadow';

const TENSION_PX = 20;
const OPEN_ANIMATION_DURATION_MS = 300;
const CLOSE_ANIMATION_DURATION_MS = 200;
const CLOSING_CLASS = 'floating-panel-closing';

export type FloatingPanelProps = HTMLAttributes<HTMLDivElement> &
  PropsWithChildren<{
    open: boolean;
    position?: PositionWithOffsetValue['position'];
    verticalOffset?: PositionWithOffsetValue['verticalOffset'];
    horizontalOffset?: PositionWithOffsetValue['horizontalOffset'];
    width?: number | string;
    height?: number | string;
    borderRadius?: number;
    hideShadow?: boolean;
    onCloseRequest: () => void;
  }>;

export const FloatingPanel = forwardRef<HTMLDivElement, FloatingPanelProps>(
  (
    {
      children,
      className,
      open,
      position = 'bottom-right',
      verticalOffset = 20,
      horizontalOffset = 20,
      width = 380,
      height = 580,
      borderRadius = 24,
      hideShadow = false,
      onCloseRequest,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const animationDuration = open
      ? OPEN_ANIMATION_DURATION_MS
      : CLOSE_ANIMATION_DURATION_MS;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const mergedRef = useMergedRef(forwardingRef, containerRef);
    const [isMounted, isClosing] = useDelayedUnmount(open, animationDuration);

    const isVisible = open && !isClosing;

    useWindowKeydown('Escape', onCloseRequest);

    return (
      isMounted && (
        <Portal>
          <Container
            {...forwardingProps}
            _position={position}
            _verticalOffset={verticalOffset}
            _horizontalOffset={horizontalOffset}
            _borderRadius={borderRadius}
            _width={width}
            _height={height}
            ref={mergedRef}
            className={isVisible ? className : `${className} ${CLOSING_CLASS}`}
            role="dialog"
            aria-modal="true"
          >
            {children}
          </Container>

          {!hideShadow && (
            <Shadow
              position={position}
              verticalOffset={verticalOffset}
              horizontalOffset={horizontalOffset}
              targetElement={containerRef}
              open={open}
              animationDuration={OPEN_ANIMATION_DURATION_MS}
            />
          )}
        </Portal>
      )
    );
  }
);

const Container = styled.div<{
  _position: PositionWithOffsetValue['position'];
  _verticalOffset: PositionWithOffsetValue['verticalOffset'];
  _horizontalOffset: PositionWithOffsetValue['horizontalOffset'];
  _borderRadius: number;
  _width: number | string;
  _height: number | string;
}>`
  ${({
    _position,
    _verticalOffset,
    _horizontalOffset,
    _borderRadius,
    _width,
    _height
  }) => {
    const { top, bottom, left, right, translate } = positionMixin({
      position: _position,
      verticalOffset: _verticalOffset,
      horizontalOffset: _horizontalOffset
    });
    const isTopPosition = _position.startsWith('top');

    return css`
      position: fixed;
      top: ${top};
      bottom: ${bottom};
      left: ${left};
      right: ${right};
      translate: ${translate};
      z-index: ${zIndexLayer.modal};
      border-radius: ${_borderRadius}px;
      box-shadow: 0px 4px 30px 0px rgba(0, 0, 0, 0.15);
      width: ${typeof _width === 'number' ? `${_width}px` : _width};
      height: ${typeof _height === 'number' ? `${_height}px` : _height};
      max-height: calc(100% - 32px);
      overflow: hidden;
      transition-property: opacity, transform;
      transition-duration: ${OPEN_ANIMATION_DURATION_MS}ms;

      &.${CLOSING_CLASS} {
        transition-duration: ${CLOSE_ANIMATION_DURATION_MS}ms;
        opacity: 0;
        transform: translateY(
          ${(isTopPosition ? -TENSION_PX : TENSION_PX) / 2}px
        );
      }

      @starting-style {
        & {
          opacity: 0;
          transform: translateY(${isTopPosition ? -TENSION_PX : TENSION_PX}px);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        & {
          transition: none;
        }
      }
    `;
  }}
`;
